import * as actionTypes from './actionTypes';

/*
 * `verdictsLoading` starts `true`, like every other list slice in this codebase: a read of every
 * appliance fires on mount, and a `false` would flash an empty table first.
 *
 * `loadedSerialNumber` is the serial filter the rows in `verdicts` were actually answered for - not
 * the one currently typed, and not the one currently committed - so the empty state cannot name an
 * appliance whose request has not come back. A blank one means the rows span every appliance.
 *
 * There is no `editedVerdict`: a verdict only moves through grant and revoke, so the form modal is
 * create-only.
 */
export const INITIAL_STATE = {
	verdicts: [],
	pagination: {},
	loadedSerialNumber: null,
	verdictsLoading: true,
	verdictsError: null,
	saving: false,
	savingError: null,
	showFormModal: false,
	verdictToGrant: null,
	verdictToRevoke: null
};

const complianceVerdictReducer = (state = INITIAL_STATE, action) => {
	switch (action.type) {
		/*
		 * The rows are cleared only when the read is for a different serial filter than the rows on
		 * screen were answered for. Leaving one appliance's verdicts beneath a caption naming another
		 * is the worst bug this page could ship; a page change, a status change or the refresh after
		 * a write cannot cause it, and blanking the table for those loses the operator's place for a
		 * one-row change. The rows kept meanwhile may be the previous page or status, which is why
		 * the table disables their actions while loading.
		 *
		 * A failed read leaves `loadedSerialNumber` null, so the read after it always clears.
		 */
		case actionTypes.START_FETCHING_COMPLIANCE_VERDICTS: {
			const sameSerial = state.loadedSerialNumber !== null
				&& state.loadedSerialNumber === action.payload?.serialNumber;

			return {
				...state,
				verdicts: sameSerial ? state.verdicts : [],
				loadedSerialNumber: sameSerial ? state.loadedSerialNumber : null,
				verdictsLoading: true,
				verdictsError: null
			};
		}
		case actionTypes.GET_COMPLIANCE_VERDICTS_SUCCESS:
			return {
				...state,
				verdicts: action.payload.verdicts,
				pagination: action.payload.pagination,
				loadedSerialNumber: action.payload.serialNumber,
				verdictsLoading: false,
				verdictsError: null
			};
		case actionTypes.GET_COMPLIANCE_VERDICTS_ERROR:
			return {
				...state,
				verdicts: [],
				pagination: {},
				loadedSerialNumber: null,
				verdictsLoading: false,
				verdictsError: action.payload
			};
		case actionTypes.START_SAVING_COMPLIANCE_VERDICT:
			return { ...state, saving: true, savingError: null };
		case actionTypes.SAVE_COMPLIANCE_VERDICT_SUCCESS:
			return { ...state, saving: false, savingError: null };
		case actionTypes.SAVE_COMPLIANCE_VERDICT_ERROR:
			return { ...state, saving: false, savingError: action.payload };
		/*
		 * An abort is the page's own unmount cleanup, so nothing is reported - but the write still
		 * owns a raised `saving` flag and possibly an open modal, and this slice outlives the
		 * route. Without lowering both, the next visit opens with a stale dialog and row actions
		 * disabled by a write nobody is running any more.
		 */
		case actionTypes.SAVE_COMPLIANCE_VERDICT_ABORTED:
			return {
				...state,
				saving: false,
				savingError: null,
				showFormModal: false,
				verdictToGrant: null,
				verdictToRevoke: null
			};
		case actionTypes.SHOW_COMPLIANCE_VERDICT_FORM_MODAL:
			return { ...state, showFormModal: true, savingError: null };
		case actionTypes.HIDE_COMPLIANCE_VERDICT_FORM_MODAL:
			return { ...state, showFormModal: false };
		case actionTypes.SHOW_COMPLIANCE_VERDICT_GRANT_MODAL:
			return { ...state, verdictToGrant: action.payload, savingError: null };
		case actionTypes.HIDE_COMPLIANCE_VERDICT_GRANT_MODAL:
			return { ...state, verdictToGrant: null };
		case actionTypes.SHOW_COMPLIANCE_VERDICT_REVOKE_MODAL:
			return { ...state, verdictToRevoke: action.payload, savingError: null };
		case actionTypes.HIDE_COMPLIANCE_VERDICT_REVOKE_MODAL:
			return { ...state, verdictToRevoke: null };
		default:
			return state;
	}
};

export default complianceVerdictReducer;
